import apiClient from '../../../core/api/client';

// The shared client resolves 4xx responses. Newer calls turn them into
// errors here so the caller's catch shows a message instead of rendering an
// error body as data.
const dataOrThrow = (res) => {
  if (res.status >= 400) {
    const err = new Error(res.data?.message || res.data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.data;
};

// ---------------------------------------------------------------
// Cards
// ---------------------------------------------------------------

export const listCards = async () => {
  const res = await apiClient.get('/api/budget/cards');
  return res.data;
};

export const createCard = async (payload) => {
  const res = await apiClient.post('/api/budget/cards', payload);
  return res.data;
};

export const updateCard = async (id, payload) => {
  const res = await apiClient.patch(`/api/budget/cards/${id}`, payload);
  return res.data;
};

export const deleteCard = async (id) => {
  await apiClient.delete(`/api/budget/cards/${id}`);
};

// ---------------------------------------------------------------
// Statements
// ---------------------------------------------------------------

export const uploadStatement = async (file, onUploadProgress) => {
  const form = new FormData();
  form.append('file', file);
  const res = await apiClient.post('/api/budget/statements', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
  return res;
};

export const listStatements = async () => {
  const res = await apiClient.get('/api/budget/statements');
  return res.data;
};

export const getStatement = async (id) => {
  const res = await apiClient.get(`/api/budget/statements/${id}`);
  return res.data;
};

export const getStatementTransactions = async (id) => {
  const res = await apiClient.get(`/api/budget/statements/${id}/transactions`);
  return res.data;
};

export const deleteStatement = async (id) => {
  await apiClient.delete(`/api/budget/statements/${id}`);
};

/** Re-runs receipt matching for a statement. Resolves to { linked }. */
export const matchStatementReceipts = async (id) => {
  const res = await apiClient.post(`/api/budget/statements/${id}/match-receipts`);
  return dataOrThrow(res);
};

// ---------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------

export const updateTransaction = async (id, payload) => {
  const res = await apiClient.patch(`/api/budget/transactions/${id}`, payload);
  return res.data;
};

/** Corrects a statement amount and clears its review flag. Resolves to the transaction. */
export const updateTransactionAmount = async (id, amount) => {
  const res = await apiClient.put(`/api/budget/transactions/${id}/amount`, { amount });
  return dataOrThrow(res);
};

/** Keeps the extracted amount and clears its review flag. Resolves to the transaction. */
export const confirmTransactionAmount = async (id) => {
  const res = await apiClient.post(`/api/budget/transactions/${id}/amount/confirm`);
  return dataOrThrow(res);
};

export const getTransactionSuggestions = async (id) => {
  const res = await apiClient.get(`/api/budget/transactions/${id}/suggestions`);
  return dataOrThrow(res);
};

export const linkReceiptToTransaction = async (transactionId, receiptId) => {
  const res = await apiClient.post(
    `/api/budget/transactions/${transactionId}/link-receipt`,
    { receiptId }
  );
  return dataOrThrow(res);
};

export const unlinkReceiptFromTransaction = async (transactionId, linkId) => {
  const res = await apiClient.delete(
    `/api/budget/transactions/${transactionId}/link-receipt/${linkId}`
  );
  dataOrThrow(res);
};

/** Card transactions the given receipts are bound to (only the caller's own). */
export const getReceiptLinks = async (receiptIds) => {
  if (!receiptIds || receiptIds.length === 0) return [];
  const res = await apiClient.get('/api/budget/receipt-links', {
    params: { receiptIds: receiptIds.join(',') },
  });
  return dataOrThrow(res);
};

// ---------------------------------------------------------------
// Categories + rules
// ---------------------------------------------------------------

export const listCategories = async () => {
  const res = await apiClient.get('/api/budget/categories');
  return res.data;
};

export const createCategory = async (payload) => {
  const res = await apiClient.post('/api/budget/categories', payload);
  return res.data;
};

export const updateCategory = async (id, payload) => {
  const res = await apiClient.patch(`/api/budget/categories/${id}`, payload);
  return res.data;
};

export const deleteCategory = async (id) => {
  await apiClient.delete(`/api/budget/categories/${id}`);
};

export const listRules = async () => {
  const res = await apiClient.get('/api/budget/category-rules');
  return res.data;
};

export const createRule = async (payload) => {
  const res = await apiClient.post('/api/budget/category-rules', payload);
  return res.data;
};

export const deleteRule = async (id) => {
  await apiClient.delete(`/api/budget/category-rules/${id}`);
};

// ---------------------------------------------------------------
// Installments
// ---------------------------------------------------------------

export const listInstallments = async (state = 'open') => {
  const res = await apiClient.get('/api/budget/installments', { params: { state } });
  return res.data;
};

export const createManualInstallment = async (payload) => {
  const res = await apiClient.post('/api/budget/installments', payload);
  return res.data;
};

export const updateInstallment = async (id, payload) => {
  const res = await apiClient.patch(`/api/budget/installments/${id}`, payload);
  return res.data;
};

export const deleteInstallment = async (id) => {
  await apiClient.delete(`/api/budget/installments/${id}`);
};

export const previewInstallmentDuplicates = async () => {
  const res = await apiClient.get('/api/budget/installments/duplicates');
  return res.data;
};

export const applyInstallmentDedup = async () => {
  const res = await apiClient.post('/api/budget/installments/dedup');
  return res.data;
};

// ---------------------------------------------------------------
// Reports
// ---------------------------------------------------------------

/**
 * Combined spending: card charges plus receipts no charge accounts for, each
 * purchase once. scope is 'mine' or 'family'.
 */
export const getSpending = async (from, to, scope = 'mine') => {
  const res = await apiClient.get('/api/budget/spending', {
    params: { from, to, scope },
  });
  return dataOrThrow(res);
};

export const debtForecast = async (months = 12) => {
  const res = await apiClient.get('/api/budget/reports/debt-forecast', {
    params: { months },
  });
  return res.data;
};
